import JSZip from "jszip";

const LIVE = /product|collection|newsletter|contact|track|search|instagram|cart|offer|delivery|review|recent|pincode|wishlist|breadcrumb|related|upsell|bundle|size|stock|variant/;
const EXT = { "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp", "image/gif": "gif", "image/avif": "avif", "image/svg+xml": "svg", "video/mp4": "mp4", "video/webm": "webm" };
const CT = { jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", webp: "image/webp", gif: "image/gif", avif: "image/avif", mp4: "video/mp4", webm: "video/webm",
  woff2: "font/woff2", woff: "font/woff", ttf: "font/ttf", otf: "font/otf", glb: "model/gltf-binary", svg: "image/svg+xml" };
const URL_RE = /url\(\s*(['"]?)([^'")]+)\1\s*\)/g;
export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

function inlineLibrary(clone, orig) {
  const frames = orig.querySelectorAll("iframe[data-testid=library-section-frame]");
  clone.querySelectorAll("iframe[data-testid=library-section-frame]").forEach((f, i) => {
    const d = new DOMParser().parseFromString(frames[i]?.getAttribute("srcdoc") || "", "text/html");
    const scripts = [...d.body.querySelectorAll("script")];
    scripts.pop()?.remove();
    const box = document.createElement("div");
    box.className = "tt-code-block";
    box.innerHTML = [...d.head.querySelectorAll("style")].map((s) => s.outerHTML).join("") + d.body.innerHTML;
    f.replaceWith(box);
  });
  clone.querySelectorAll("[data-testid=library-section-select-overlay]").forEach((x) => x.remove());
}

const clean = (c) => { [c, ...c.querySelectorAll("*")].forEach((e) => [...e.attributes].forEach((a) => { if (/^x-/.test(a.name)) e.removeAttribute(a.name); })); return c; };

export function capturePage(frame, page, title) {
  const shell = frame.firstElementChild;
  const parts = [];
  const mark = (el, live) => { if (!el) return; const c = clean(el.cloneNode(true)); c.setAttribute("data-tt-live", live); parts.push(c.outerHTML); };
  mark(shell.querySelector("[data-testid=store-announcement]"), "announcement");
  mark(shell.querySelector("[data-testid=store-brand-tile]"), "brand");
  mark(shell.querySelector("header"), "header");
  const byId = Object.fromEntries((page.sections || []).map((s) => [s.id, s]));
  frame.querySelectorAll("[data-sec-id]").forEach((w) => {
    const s = byId[w.getAttribute("data-sec-id")];
    const sec = w.querySelector("section");
    if (!s || !sec) return;
    const c = clean(sec.cloneNode(true));
    inlineLibrary(c, sec);
    if (s.hidden) c.setAttribute("data-tt-hidden", "1");
    if (LIVE.test(s.type)) {
      c.setAttribute("data-tt-live", s.type);
      c.setAttribute("data-tt-settings", JSON.stringify(s.settings || {}));
      c.setAttribute("data-tt-style", JSON.stringify(s.style || {}));
    } else c.setAttribute("data-tt-section", s.type);
    c.setAttribute("data-tt-id", s.id);
    parts.push(c.outerHTML);
  });
  mark(shell.querySelector("footer"), "footer");
  return `<!doctype html>\n<html lang="en">\n<head>\n<meta charset="utf-8">\n<meta name="viewport" content="width=device-width, initial-scale=1">\n<title>${esc(title)}</title>\n<link rel="stylesheet" href="../assets/site.css">\n</head>\n<body>\n<div class="tt-shell" style="${esc(shell.getAttribute("style") || "")}">\n${parts.join("\n")}\n</div>\n</body>\n</html>\n`;
}

function siteCss() {
  const imports = [], rules = [];
  for (const sh of document.styleSheets) {
    try { rules.push([...sh.cssRules].map((r) => r.cssText).join("\n")); } catch { if (sh.href) imports.push(`@import url('${sh.href}');`); }
  }
  return imports.join("\n") + "\n" + rules.join("\n");
}

const hiRes = (u) => {
  try {
    const x = new URL(u, window.location.origin);
    if (/images\.unsplash\.com|images\.pexels\.com/.test(x.hostname)) { ["w", "h", "q", "dpr", "fit", "crop", "cs"].forEach((k) => x.searchParams.delete(k)); x.searchParams.set("w", "2400"); x.searchParams.set("q", "90"); }
    return x.href;
  } catch { return u; }
};

export async function buildZip({ pages, theme, store, onProgress }) {
  const zip = new JSZip();
  const urls = new Set();
  const all = Object.values(pages).join("\n");
  all.replace(/(?:src|poster)="([^"]+)"/g, (_, u) => { urls.add(u); return _; });
  all.replace(/url\(\s*(?:&quot;|['"])?([^'")&]+)/g, (_, u) => { urls.add(u); return _; });
  const map = {};
  let n = 0;
  const list = [...urls].filter((u) => !u.startsWith("data:") && !u.startsWith("blob:")).slice(0, 400);
  for (const u of list) {
    onProgress?.(`Downloading images ${++n}/${list.length}…`);
    try {
      const r = await fetch(hiRes(u.replace(/&amp;/g, "&")));
      if (!r.ok) throw new Error();
      const b = await r.blob();
      const ext = EXT[b.type.split(";")[0]];
      if (!ext) throw new Error();
      const name = `img-${String(n).padStart(3, "0")}.${ext}`;
      zip.file(`images/${name}`, b);
      map[u] = `../images/${name}`;
    } catch { map[u] = new URL(u.replace(/&amp;/g, "&"), window.location.origin).href; }
  }
  const entries = Object.entries(map).sort((a, b) => b[0].length - a[0].length);
  const swap = (h) => entries.reduce((acc, [a, b]) => acc.split(`"${a}"`).join(`"${b}"`).split(`'${a}'`).join(`'${b}'`).split(`(${a})`).join(`(${b})`).split(`&quot;${a}&quot;`).join(`&quot;${b}&quot;`), h).replace(/\s(srcset|sizes)="[^"]*"/g, "");
  Object.entries(pages).forEach(([k, h]) => zip.file(`pages/${k}.html`, swap(h)));
  zip.file("assets/site.css", siteCss());
  zip.file("tt-theme.json", JSON.stringify({ store: store.name, settings: theme.settings, pages: Object.fromEntries(Object.entries(theme.pages).map(([k, p]) => [k, p.title])) }, null, 2));
  zip.file("README.md", README(store.name));
  onProgress?.("Packing zip…");
  return zip.generateAsync({ type: "blob" });
}

const README = (name) => `# ${name}: website code

This is the full code for every page of this Two Thread store, with its images.
Open any file in \`pages/\` in a browser to see it.

## How to improve it (instructions for an AI or a developer)
You can change anything in this code: layout, HTML, CSS, animations, 3D (Three.js, GSAP and so on), fonts, colours, images and new sections.
Keep these rules so the code can be uploaded back into the store:

1. Keep one file per page in \`pages/\` (home.html, product.html, ...). The file name is the page.
2. Every top-level block inside \`<div class="tt-shell">\` becomes its own section. You may add, remove or reorder blocks.
3. Blocks marked \`data-tt-live="..."\` (header, footer, announcement, brand, product lists, collections, forms) are LIVE store parts.
   The store fills them with real products, cart and forms, so your HTML inside them is ignored.
   Keep the element and its data-tt-* attributes where you want it on the page. You can edit the JSON in data-tt-settings (for example "title").
   Change their colours and fonts in \`tt-theme.json\` under "settings" (hex colours, Google font names).
   Hidden sections carry data-tt-hidden; keep that attribute to keep them hidden.
4. Each non-live block runs in its own sealed frame:
   - Put CSS in \`<style>\` tags or in \`assets/*.css\`. Put JavaScript in \`<script>\` tags or local .js files.
   - External libraries must load from cdn.jsdelivr.net, unpkg.com, cdnjs.cloudflare.com, esm.sh or ga.jspm.io.
   - The frame grows to fit its content and cannot scroll itself. Start scroll-based animations when the block becomes visible:
     listen for \`window.addEventListener('tt:visible', ...)\` or use the CSS class \`body.tt-visible\`.
   - Links that open store pages should use paths like /shop, /collections/<handle> or /products/<handle>.
5. Put images, videos, fonts and .glb 3D models in \`images/\` (or any folder) and reference them with relative paths. They are uploaded automatically.
6. Return the whole folder as a .zip file.
`;

const norm = (base, ref) => {
  const out = [];
  `${base}/${ref}`.split("/").forEach((p) => { if (p === "..") out.pop(); else if (p && p !== ".") out.push(p); });
  return out.join("/");
};
const isLocal = (u) => u && !/^(https?:|data:|blob:|#|mailto:|tel:|\/\/)/i.test(u) && !u.startsWith("/");

export async function readSiteZip(file, uploadAsset, onProgress) {
  const zip = await JSZip.loadAsync(file);
  const names = Object.keys(zip.files).filter((n) => !zip.files[n].dir && !n.includes("__MACOSX"));
  const cache = {};
  const asset = async (path) => {
    if (path in cache) return cache[path];
    const f = zip.file(path);
    const ext = path.split(".").pop().toLowerCase();
    if (!f || !CT[ext]) return (cache[path] = null);
    const blob = await f.async("blob");
    if (ext === "svg") return (cache[path] = `data:image/svg+xml;base64,${await f.async("base64")}`);
    onProgress?.(`Uploading ${path.split("/").pop()}…`);
    return (cache[path] = await uploadAsset(new File([blob], path.split("/").pop(), { type: CT[ext] })));
  };
  const fixCss = async (css, dir) => {
    let out = css;
    for (const m of [...css.matchAll(URL_RE)]) if (isLocal(m[2])) { const u = await asset(norm(dir, m[2].split(/[?#]/)[0])); if (u) out = out.split(m[0]).join(`url('${u}')`); }
    return out;
  };
  const pages = {};
  const htmls = names.filter((n) => /\.html?$/i.test(n));
  for (const path of htmls) {
    const dir = path.split("/").slice(0, -1).join("/");
    const doc = new DOMParser().parseFromString(await zip.file(path).async("string"), "text/html");
    for (const l of [...doc.querySelectorAll("link[rel~=stylesheet][href]")]) {
      const h = l.getAttribute("href");
      if (!isLocal(h)) continue;
      const p = norm(dir, h.split(/[?#]/)[0]);
      const f = zip.file(p);
      const st = doc.createElement("style");
      st.textContent = f ? await fixCss(await f.async("string"), p.split("/").slice(0, -1).join("/")) : "";
      l.replaceWith(st);
    }
    for (const st of doc.querySelectorAll("style")) st.textContent = await fixCss(st.textContent, dir);
    for (const s of [...doc.querySelectorAll("script[src]")]) {
      const h = s.getAttribute("src");
      if (!isLocal(h)) continue;
      const f = zip.file(norm(dir, h.split(/[?#]/)[0]));
      s.removeAttribute("src");
      s.textContent = f ? await f.async("string") : "";
    }
    for (const el of doc.querySelectorAll("[src], [poster], [href]")) {
      for (const a of ["src", "poster", "href"]) {
        const v = el.getAttribute(a);
        if (!isLocal(v) || (a === "href" && !/\.(jpe?g|png|webp|gif|avif|mp4|webm|glb|svg)$/i.test(v))) continue;
        const u = await asset(norm(dir, v.split(/[?#]/)[0]));
        if (u) el.setAttribute(a, u);
      }
      el.removeAttribute("srcset");
    }
    for (const el of doc.querySelectorAll("[style*='url(']")) el.setAttribute("style", await fixCss(el.getAttribute("style"), dir));
    const key = path.split("/").pop().replace(/\.html?$/i, "").toLowerCase();
    pages[key === "index" ? "home" : key] = "<!doctype html>" + doc.documentElement.outerHTML;
  }
  const tj = names.find((n) => /tt-theme\.json$/i.test(n));
  let settings = null;
  if (tj) { try { settings = JSON.parse(await zip.file(tj).async("string")).settings || null; } catch { settings = null; } }
  return { pages, theme_settings: settings };
}
